import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Row, Col } from "react-bootstrap";
import { checkRegistrationExpiry } from "../../utils/RegistrationExpiryHelper";
import "../../../css/shinewell-dashboard.css";

const Home = () => {
  const [sessionData, setSessionData] = useState(() => {
    try {
      return {
        firm: JSON.parse(localStorage.getItem("selectedFirm") || "null"),
        branch: JSON.parse(localStorage.getItem("selectedBranch") || "null"),
        month: localStorage.getItem("selectedPayrollMonth") || "",
        year: localStorage.getItem("selectedPayrollYear") || "",
      };
    } catch {
      return { firm: null, branch: null, month: "", year: "" };
    }
  });

  const userData = (() => {
    try {
      return JSON.parse(localStorage.getItem("authUser") || "{}");
    } catch {
      return {};
    }
  })();

  const syncSession = useCallback(() => {
    try {
      setSessionData({
        firm: JSON.parse(localStorage.getItem("selectedFirm") || "null"),
        branch: JSON.parse(localStorage.getItem("selectedBranch") || "null"),
        month: localStorage.getItem("selectedPayrollMonth") || "",
        year: localStorage.getItem("selectedPayrollYear") || "",
      });
    } catch {
      setSessionData({ firm: null, branch: null, month: "", year: "" });
    }
  }, []);

  useEffect(() => {
    window.addEventListener("storage", syncSession);
    window.addEventListener("sessionChange", syncSession);
    return () => {
      window.removeEventListener("storage", syncSession);
      window.removeEventListener("sessionChange", syncSession);
    };
  }, [syncSession]);

  const isSessionReady = !!(sessionData.firm && sessionData.month && sessionData.year);
  const expiryAlert = checkRegistrationExpiry(sessionData.branch);

  const quickLinks = [
    {
      title: "Single Payroll Entry",
      desc: "Individual employee attendance, workings, deductions & net pay processing.",
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="4" y="2" width="16" height="20" rx="2" ry="2"/>
          <line x1="8" y1="6" x2="16" y2="6"/>
          <line x1="16" y1="14" x2="16" y2="18"/>
          <path d="M16 10h.01M12 10h.01M8 10h.01M12 14h.01M8 14h.01M12 18h.01M8 18h.01"/>
        </svg>
      ),
      url: "/Payroll",
    },
    {
      title: "Wages Register",
      desc: "Department wise wages register, legal registers & summaries.",
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
          <polyline points="14 2 14 8 20 8"/>
          <line x1="16" y1="13" x2="8" y2="13"/>
          <line x1="16" y1="17" x2="8" y2="17"/>
          <polyline points="10 9 9 9 8 9"/>
        </svg>
      ),
      url: "/WagesRegisterDeptWise",
    },
    {
      title: "Wages Slip (Individual)",
      desc: "Generate and print detailed individual salary slips with breakdown.",
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="4" width="20" height="16" rx="2"/>
          <circle cx="8" cy="10" r="2"/>
          <path d="M12 11h6M12 15h4M6 16c0-2 2-3 4-3"/>
        </svg>
      ),
      url: "/WagesSlipIndividual",
    },
    {
      title: "PF & ESI Compliance",
      desc: "PF Challan reports Form 11, Form 12 & ESI monthly deduction returns.",
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          <polyline points="9 12 11 14 15 10"/>
        </svg>
      ),
      url: "/PfChallanReport",
    },
    {
      title: "Employee Master",
      desc: "Manage workforce records, salary structures, & statutory numbers.",
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
          <circle cx="9" cy="7" r="4"/>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
        </svg>
      ),
      url: "/EmployeeMaster",
    },
  ];

  return (
    <div className="container-fluid shinewell-dashboard-container">
      {/* Welcome Hero Banner Card */}
      <div className="shinewell-hero-card">
        <div className="shinewell-user-meta">
          <span className="shinewell-username-badge">
            [{userData?.name || "User Name"}]
          </span>
          <span>
            {new Date().toLocaleDateString("en-IN", {
              weekday: "long",
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </span>
        </div>

        <h2 className="shinewell-hero-title">
          Welcome! 👋
        </h2>
        <p className="shinewell-hero-desc">
          Welcome to the Payroll Management Portal. Manage wage records, statutory reports, employee attendance, and compliance efficiently.
        </p>
      </div>

      {/* Quick Access Modules Section */}
      <div className="mb-4">
        <div className="shinewell-section-header">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="3" y="3" width="8" height="8" rx="2" fill="#FF4D12"/>
            <rect x="13" y="3" width="8" height="8" rx="2" fill="#FF4D12"/>
            <rect x="3" y="13" width="8" height="8" rx="2" fill="#FF4D12"/>
            <rect x="13" y="13" width="8" height="8" rx="2" fill="#FF4D12"/>
          </svg>
          <h5 className="shinewell-section-title">
            Quick Access Modules
          </h5>
        </div>

        <Row className="g-3">
          {quickLinks.map((item, idx) => (
            <Col key={idx} xl={4} md={6} sm={12}>
              <Link to={item.url} className="shinewell-module-card">
                <div className="d-flex align-items-center gap-3">
                  <div className="shinewell-module-icon-box">
                    {item.icon}
                  </div>
                  <div>
                    <h6 className="shinewell-module-title">{item.title}</h6>
                    <p className="shinewell-module-desc">{item.desc}</p>
                  </div>
                </div>
                <i className="fa fa-chevron-right shinewell-module-chevron" />
              </Link>
            </Col>
          ))}
        </Row>
      </div>

      {/* Session Persistence Footer Bar */}
      <div className="shinewell-info-bar">
        <div className="shinewell-info-text">
          <i className="fa fa-info-circle text-primary fs-14" />
          <span>
            <strong>Session Persistence:</strong> Selected Firm, Branch, Month, and Year are saved automatically in your session for all payroll modules and reports.
          </span>
        </div>
        <div className="d-flex align-items-center gap-2">
          <span className="shinewell-shortcut-badge">Alt + K</span>
          <span className="text-muted fs-12">for shortcuts</span>
        </div>
      </div>
    </div>
  );
};

export default Home;